import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import AccountBadge from './Components/AccountBadge'
import PhoneButton from '../../Components/PhoneButton'
import TableBadge from './Components/TableBadge'
import TableSelector from './Components/TableSelector'
import Button from '../../Components/Button'
import Steps from './Components/Steps'
import Layout from './Layout'
import { generateTimeOptions } from './utils/formatters'

export default function Home({ account, source, qr_info, indoor_tables }) {
  const isIndoorQr = qr_info?.is_indoor
  const hasIndoorTables = indoor_tables && indoor_tables.length > 0

  const [pickupTime, setPickupTime] = useState(
    qr_info?.suggested_pickup_time || '12:30'
  )

  // Dine-in mode: true = dine in (table), false = pickup (outdoor)
  const [dineIn, setDineIn] = useState(isIndoorQr || false)
  const [selectedTable, setSelectedTable] = useState(null)

  const tableNumber = isIndoorQr
    ? qr_info.table_number
    : selectedTable?.table_number

  const tableSource = isIndoorQr
    ? source
    : selectedTable?.qr_code

  const handleGetStarted = () => {
    const data = { source }

    if (isIndoorQr || (dineIn && selectedTable)) {
      data.table_number = tableNumber
      data.table_source = tableSource
      // Use suggested pickup time for indoor (15 min)
      data.pickup_time = isIndoorQr
        ? qr_info.suggested_pickup_time
        : ''
    } else {
      data.pickup_time = pickupTime
      data.table_number = ''
      data.table_source = ''
    }

    router.visit(`/${account.slug}/order/food`, {
      method: 'get',
      data
    })
  }

  const canStart = isIndoorQr || !dineIn || selectedTable

  // Show message if account cannot accept orders
  if (!account.can_accept_orders) {
    return (
      <>
        <Head title={`Order - ${account.name}`} />
        <Layout account={account}>
          <div className="flex flex-col min-h-screen">
            <div className="bg-sky-900 text-white px-6 py-8 text-center flex flex-col items-center justify-center">
              <div className="mx-auto mb-4">
                <AccountBadge name={account.name} logoUrl={account.logo_url} size="md" />
              </div>
              <h1 className="text-xl font-bold mb-1">
                Welcome to the {account.name.replace('Golf Club', '')}
              </h1>
            </div>

            <div className="flex-1 px-6 py-8 flex flex-col items-center justify-center bg-gray-100 md:rounded-2xl">
              <div className="text-center max-w-sm">
                <h2 className="text-xl font-semibold text-gray-700 mb-3">
                  Online ordering coming soon
                </h2>
                <p className="text-gray-500 mb-6">
                  {account.name} is setting up online payments. Please check back later or call to place an order.
                </p>
                <PhoneButton phoneNumber={account.phone} variant="primary" size="lg">
                  Call to Order: {account.phone}
                </PhoneButton>
              </div>
            </div>
          </div>
        </Layout>
      </>
    )
  }

  return (
    <>
      <Head title={`Order - ${account.name}`} />
      <Layout account={account}>
        <div className="flex flex-col min-h-screen">
          <div className="bg-sky-900 text-white px-6 py-5 md:py-8 text-center flex flex-col items-center justify-center">
            <div className="mx-auto mb-4">
              <AccountBadge name={account.name} logoUrl={account.logo_url} size="md" />
            </div>
            <h1 className="text-xl font-bold mb-1">
              Welcome to the {account.name.replace('Golf Club', '')}
            </h1>
            <h2 className="text-lg opacity-90">
              {isIndoorQr ? 'Table Ordering' : 'Preorder Service'}
            </h2>
          </div>

        <div className="flex-1 px-6 py-8 flex flex-col bg-gray-100 md:rounded-2xl">
          <div className="mb-4 md:mb-8">
            <Steps isIndoor={isIndoorQr || (dineIn && hasIndoorTables)} />
          </div>

          {qr_info?.order_promo_text && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6">
              <p className="text-blue-900 text-sm text-center">
                {qr_info.order_promo_text}
              </p>
            </div>
          )}

          {qr_info?.sponsors && qr_info.sponsors.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 text-center mb-3">
                Sponsored by
              </h3>
              <div className="space-y-3">
                {qr_info.sponsors.map((sponsor) => (
                  <a
                    key={sponsor.id}
                    href={sponsor.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${sponsor.name} (opens in new tab)`}
                    className="block bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {sponsor.logo_url && (
                        <img
                          src={sponsor.logo_url}
                          alt=""
                          className="w-12 h-12 object-contain rounded"
                        />
                      )}
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{sponsor.name}</div>
                        {sponsor.short_description && (
                          <div className="text-sm text-gray-600 mt-1">
                            {sponsor.short_description}
                          </div>
                        )}
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="mt-2">
            {isIndoorQr ? (
              /* QR scanned at indoor table — show confirmed table badge */
              <div className="mb-6 flex justify-center">
                <TableBadge
                  tableNumber={qr_info.table_number}
                  subtitle={qr_info.display_name}
                />
              </div>
            ) : hasIndoorTables ? (
              /* No QR but account has indoor tables — two mode cards */
              <div className="mb-6 space-y-3">
                {/* Collection card */}
                <button
                  type="button"
                  onClick={() => setDineIn(false)}
                  className={`w-full text-left rounded-xl p-4 transition-all duration-200 ${
                    !dineIn
                      ? 'bg-white shadow-md ring-2 ring-sky-500'
                      : 'bg-white/60 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-7 h-7 text-sky-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                    <div>
                      <div className="font-semibold text-gray-900">Preorder your food</div>
                      <div className="text-sm text-gray-500">We'll have it ready for you</div>
                    </div>
                  </div>
                  {!dineIn && (
                    <div className="mt-3 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                      <label htmlFor="pickup-time" className="block text-gray-900 text-center mb-3 font-medium">
                        Your order will be ready at
                      </label>
                      <select
                        id="pickup-time"
                        value={pickupTime}
                        onChange={(e) => setPickupTime(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border-0 bg-white text-gray-900 font-medium text-center focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                      >
                        {generateTimeOptions().map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </button>

                {/* Table card */}
                <button
                  type="button"
                  onClick={() => setDineIn(true)}
                  className={`w-full text-left rounded-xl p-4 transition-all duration-200 ${
                    dineIn
                      ? 'bg-white shadow-md ring-2 ring-sky-500'
                      : 'bg-white/60 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-7 h-7 text-sky-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                    </svg>
                    <div>
                      <div className="font-semibold text-gray-900">Order to Table</div>
                      <div className="text-sm text-gray-500">We'll bring it to you</div>
                    </div>
                  </div>
                  {dineIn && (
                    <div className="mt-3 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                      <TableSelector
                        tables={indoor_tables}
                        value={selectedTable?.qr_code}
                        onSelect={setSelectedTable}
                      />
                    </div>
                  )}
                </button>
              </div>
            ) : (
              /* Outdoor-only club — existing pickup time selector */
              <div className="mb-6">
                <label htmlFor="pickup-time" className="block text-gray-900 text-center mb-3 font-medium">
                  Your order will be ready at
                </label>
                <select
                  id="pickup-time"
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border-0 bg-white text-gray-900 font-medium text-center focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                >
                  {generateTimeOptions().map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            )}

            <Button
              onClick={handleGetStarted}
              theme="dark"
              size="lg"
              fullWidth
              disabled={!canStart}
              className="shadow-lg mb-2"
            >
              Get Started
            </Button>

            <PhoneButton
              phoneNumber={account.phone}
              size="sm"
              className="!bg-transparent !text-gray-500 !shadow-none !font-medium"
            >
              Or call to order
            </PhoneButton>
          </div>
        </div>
        </div>
      </Layout>
    </>
  )
}
